import { omitBy } from 'lodash';

import * as cookie from 'js/lib/cookie';
import * as user from 'js/lib/user';
import { randomUUID as uuid } from 'js/lib/uuid';

import type Client from './client';
import type Device from './device';
import type Events from './events';
import type Session from './session';

type App = string;

type Options<K extends keyof Events> = {
  app: App;
  appVersion?: string;
  client: Client;
  device: Device;
  key: K;
  session: Session;
  value: Events[K];
};

type EnvelopeJSON = {
  app: App;
  appVersion?: string;
  clientTimestamp: number;
  clientType: string;
  clientVersion: string;
  deviceId: string;
  guid: string;
  initialReferrer?: string;
  key: string;
  referrerUrl?: string;
  screenSize: string;
  sequence: number;
  url: string;
  userId?: string;
  value: unknown;
  visitId: string;
};

/**
 * Class to wrap the data sent to the server.
 *
 * This class encapsulates the event data that is sent to the server, including
 * information about the app, client, device, session, and event value. It also
 * provides a method to serialize the data to JSON format.
 */
class Envelope<K extends keyof Events = keyof Events> {
  /**
   * The app name.
   */
  declare app: App;

  /**
   * The app version. This will be a unique string of characters based on the built artifact for preview or prod.
   */
  declare appVersion?: string;

  /**
   * The client that sent the event.
   */
  declare client: Client;

  /**
   * The device that sent the event.
   */
  declare device: Device;

  /**
   * The key of the event.
   */
  declare key: K;

  /**
   * The referrer of the event.
   * @private
   */
  declare private readonly referrer: string;

  /**
   * The sequence of the event.
   * @private
   */
  declare private readonly sequence: number;

  /**
   * The session of the event.
   */
  declare session: Session;

  /**
   * The timestamp of the event.
   * @private
   */
  declare private readonly timestamp: number;

  /**
   * The URL of the event.
   * @private
   */
  declare private readonly url: string;

  /**
   * The value of the event.
   */
  declare value: Events[K];

  /**
   * Constructor for the Envelope class.
   *
   * @param options - An object containing the app, client, device, event key, session, and event value.
   */
  constructor(options: Omit<Options<K>, 'value'>) {
    this.app = options.app;
    this.appVersion = options.appVersion;
    this.client = options.client;
    this.device = options.device;
    this.key = options.key;
    // We generate the sequence in the constructor so that it is always
    // incremented when the envelope is created.
    this.sequence = options.session.getSequence();
    // Referrer could change before the event is sent, so we store it here.
    this.referrer = options.session.referrer;
    this.session = options.session;
    // Store the timestamp when the envelope is created.
    this.timestamp = Date.now();
    // URL could change before the event is sent, so we store it here.
    this.url = options.session.url;
  }

  /**
   * Serialize the envelope to JSON.
   *
   * This method converts the envelope data to a JSON object, removing any undefined or empty values.
   *
   * @returns A JSON representation of the envelope.
   */
  toJSON(): EnvelopeJSON {
    // Remove undefined or empty values
    return omitBy(
      {
        app: this.app,
        appVersion: this.appVersion,
        clientTimestamp: this.timestamp,
        clientType: this.client.type,
        clientVersion: this.client.version,
        // If no __204u cookie, use device uuid as cookieId otherwise to explicitly set cookieId in the event
        cookieId: !cookie.get('__204u') ? this.device.uuid : undefined,
        deviceId: this.device.uuid,
        guid: uuid(),
        initialReferrer: this.session.initialReferrer,
        // We introduce the `eventingv3.` prefix to the key for API compatibility reasons.
        key: `eventingv3.${this.key}`,
        referrerUrl: this.referrer,
        screenSize: this.device.screenSize,
        sequence: this.sequence,
        url: this.url,
        userId: user.get()?.id?.toString(),
        value: this.value,
        viewportSize: this.device.viewportSize,
        visitId: this.session.uuid,
      },
      (value: unknown) => !value
    ) as EnvelopeJSON;
  }
}

export default Envelope;
